import _ from 'lodash';
import { DEFAULT_SPECIE, SpeciesType } from 'app/constants/species';
import { ProcessingKind } from 'app/interfaces/IImageProcessor';
import { ItemDataType } from 'rsuite/esm/MultiCascadeTree';
import { IntlShape } from 'react-intl';

export type MainRegion = keyof typeof XRAY_REGIONS;
export type SubRegion = typeof XRAY_REGIONS[keyof typeof XRAY_REGIONS][number];
export type View = 'face' | 'profil';
export type Orientation = 'left' | 'right';

const XRAY_REGIONS = {
  thorax: ['thorax'],
  abdomen: ['abdomen', 'Bassin', 'gestation'],
  crane: ['crane'],
  tail: ['tail'],
  rachis: ['Cervical', 'Thoraco-Lombaire'],
  membre_pelvien_gauche: ['Hanche-Fémur', 'Grasset', 'Tibia', 'Tarse-Doigts'],
  membre_pelvien_droit: ['Hanche-Fémur', 'Grasset', 'Tibia', 'Tarse-Doigts'],
  membre_thoracique_gauche: ['Epaule', 'Coude', 'Radius-Ulna', 'Carpe-Doigts'],
  membre_thoracique_droit: ['Epaule', 'Coude', 'Radius-Ulna', 'Carpe-Doigts'],
  entire_body: [],
} as const;
Object.freeze(XRAY_REGIONS);

export const CONFIGURABLE_XRAY_REGIONS = {
  thorax: ['thorax'],
  abdomen: ['abdomen', 'Bassin', 'gestation'],
  bones: {
    tail: ['tail'],
    membre_pelvien_gauche: ['Hanche-Fémur', 'Grasset', 'Tibia', 'Tarse-Doigts'],
    membre_pelvien_droit: ['Hanche-Fémur', 'Grasset', 'Tibia', 'Tarse-Doigts'],
    membre_thoracique_gauche: ['Epaule', 'Coude', 'Radius-Ulna', 'Carpe-Doigts'],
    membre_thoracique_droit: ['Epaule', 'Coude', 'Radius-Ulna', 'Carpe-Doigts'],
    crane: ['crane'],
    rachis: ['Cervical', 'Thoraco-Lombaire'],
  },
  entire_body: [],
} as const;
Object.freeze(CONFIGURABLE_XRAY_REGIONS);

const VIEWS = ['face', 'profil'];

const DEFAULT_ANATOMIC_REGION = 'entire_body';
export const DEFAULT_ANATOMIC_REGION_WITH_SPECIE = `${DEFAULT_SPECIE} ${DEFAULT_ANATOMIC_REGION}`;

export const SAME_THICKNESS_AREAS = [
  ...['Hanche-Fémur', 'Grasset', 'Tibia', 'Tarse-Doigts'].map((subArea) => [
    ['membre_pelvien_gauche', subArea],
    ['membre_pelvien_droit', subArea],
  ]),
  ...['Epaule', 'Coude', 'Radius-Ulna', 'Carpe-Doigts'].map((subArea) => [
    ['membre_thoracique_gauche', subArea],
    ['membre_thoracique_droit', subArea],
  ]),
];

const THORAX_REGIONS = ['thorax'];
const ABDOMEN_REGIONS = ['abdomen'];

const BONES_REGIONS = [
  'crane',
  'rachis',
  'membre_pelvien_gauche',
  'membre_pelvien_droit',
  'membre_thoracique_gauche',
  'membre_thoracique_droit',
  'tail',
] as const;

const LEFT_ORIENTATION_REGION = ['membre_pelvien_gauche', 'membre_thoracique_gauche'];
const RIGHT_ORIENTATION_REGION = ['membre_pelvien_droit', 'membre_thoracique_droit'];

type AnatomicRegionDescription = {
  specie: SpeciesType;
  mainRegion: MainRegion;
  subRegion?: SubRegion;
  view?: View;
  orientation?: Orientation;
};

/**
 * Decompose the anatomic region string and return it into object format.
 * @param {string} anatomicRegionString A string that represent the anatomical region being considered, it can have 2 forms:
 * - "{specie} {mainRegion} {subRegion} {view}"
 * - "{specie} {mainRegion} {view}"
 * @returns {{ specie:string, mainRegion:string, subRegion:string, view:string }} AnatomicRegion as an object
 */
const getAnatomicRegionFromString = (anatomicRegionString: string): AnatomicRegionDescription => {
  if (!anatomicRegionString) {
    return { specie: DEFAULT_SPECIE, mainRegion: DEFAULT_ANATOMIC_REGION };
  }
  const [specie, mainRegion, ...anatomicRegionDetails] = anatomicRegionString.split(' ');
  let view;
  let subRegion;
  if (VIEWS.includes(anatomicRegionDetails.at(-1))) {
    [view, subRegion] = anatomicRegionDetails.reverse();
  } else {
    [subRegion] = anatomicRegionDetails;
  }
  let orientation;
  if (LEFT_ORIENTATION_REGION.includes(mainRegion)) orientation = 'left';
  if (RIGHT_ORIENTATION_REGION.includes(mainRegion)) orientation = 'right';

  return { specie, mainRegion, subRegion, view, orientation } as AnatomicRegionDescription;
};

export const anatomicRegionToString = ({
  specie,
  mainRegion = DEFAULT_ANATOMIC_REGION,
  subRegion = undefined,
  view = undefined,
}: AnatomicRegionDescription) => _.filter([specie, mainRegion, subRegion, view]).join(' ');

/**
 *
 * @param {string} anatomicRegion
 * @returns {import('app/interfaces/IImageProcessor').ProcessingKind}
 */
const getProcessingFromAnatomicRegion = (anatomicRegion: string): ProcessingKind => {
  const { specie, mainRegion, subRegion } = getAnatomicRegionFromString(anatomicRegion);
  const speciePrefix = specie === 'dog' ? 'dog' : 'cat';
  if (subRegion === 'Bassin' || subRegion === 'gestation') return 'bones';
  if (subRegion === 'abdomen') return `${speciePrefix}_abdomen`;
  if (mainRegion === 'thorax') return `${speciePrefix}_thorax`;
  if (mainRegion === 'crane' && specie === 'dog') return `dog_skull`;
  if ((BONES_REGIONS as readonly string[]).includes(mainRegion)) return `bones`;

  return 'default';
};

const getDefaultAnatomicRegion = (specie = DEFAULT_SPECIE) =>
  `${specie} ${DEFAULT_ANATOMIC_REGION}`;

export const isSameAnatomicThickness = (
  anatomicRegionString1: string,
  anatomicRegionString2: string
) => {
  const anatomicRegion1 = _.omit(getAnatomicRegionFromString(anatomicRegionString1), [
    'orientation',
  ]);
  const anatomicRegion2 = _.omit(getAnatomicRegionFromString(anatomicRegionString2), [
    'orientation',
  ]);

  SAME_THICKNESS_AREAS.forEach((areas) => {
    const mainArea = areas[0];
    areas.forEach(([mainRegion, subRegion]) => {
      [anatomicRegion1, anatomicRegion2].forEach((anatomicRegion) => {
        if (anatomicRegion.mainRegion === mainRegion && anatomicRegion.subRegion === subRegion) {
          // @ts-ignore
          anatomicRegion.mainRegion = mainArea[0];
          // @ts-ignore
          anatomicRegion.subRegion = mainArea[1];
        }
      });
    });
  });

  return _.isEqual(anatomicRegion1, anatomicRegion2);
};

/**
 * Retrieve the anatomic region upper representation as an array of strings.
 * The intended use is to find which acquisition constants to use for a specific anatomic region.
 * @param {string} anatomicRegionRepr - A string that represent the anatomical region being considered, it can have multiple forms:
 * - "{specie} {mainRegion} {subRegion} {view}"
 * - "{specie} {mainRegion} {view}"
 * - "{specie} {mainRegion} {subRegion}"
 */
export function getAnatomicRegionTree(anatomicRegionRepr: string) {
  const { mainRegion, subRegion } = getAnatomicRegionFromString(anatomicRegionRepr);
  return ['default', mainRegion, ...(subRegion ? [`${mainRegion} ${subRegion}`] : [])];
}

type CascaderData = ItemDataType<string>[];

function buildCascaderTree(obj: Record<string, any> | string[], valuePrefix = ''): CascaderData {
  return _.map(obj, (value, key) => {
    if (Array.isArray(obj)) {
      return {
        label: `exam.body_part.${value}`,
        value: `${valuePrefix}${value}`,
      };
    } else {
      if (typeof value === 'object') {
        const children = buildCascaderTree(
          value as Record<string, object>,
          `${valuePrefix}${key} `
        );
        return {
          label: `exam.body_part.${key}`,
          value: `${valuePrefix}${key}`,
          children: (children ?? []).length > 1 ? children : undefined,
        };
      }
    }
  });
}

export const ANATOMIC_REGION_CASCADER_TREE: ItemDataType<string>[] = Object.freeze([
  {
    label: 'general.default',
    value: 'default',
    children: buildCascaderTree(CONFIGURABLE_XRAY_REGIONS),
  },
]) as ItemDataType<string>[];

export function localizeCascaderTreeData(data: CascaderData, intl: IntlShape): CascaderData {
  return data?.map((item) => ({
    ...item,
    label: intl.formatMessage({ id: item.label as string }),
    children: localizeCascaderTreeData(item.children, intl),
  }));
}

export {
  XRAY_REGIONS,
  VIEWS,
  DEFAULT_ANATOMIC_REGION,
  THORAX_REGIONS,
  BONES_REGIONS,
  ABDOMEN_REGIONS,
  getProcessingFromAnatomicRegion,
  getAnatomicRegionFromString,
  getDefaultAnatomicRegion,
};
